(() => {
  const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
  const recoveryLifetime = 30 * 60 * 1000;
  const fieldIds = [
    'asb-name', 'asb-company', 'asb-email', 'asb-phone',
    'asb-product-link', 'asb-competitor-link', 'asb-price', 'asb-quantity', 'asb-body', 'asb-confirm'
  ];
  const moneyPattern = /^\s*\$?\s*(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?\s*$/;
  const domainPattern = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d{1,5})?(?:[/?#][^\s]*)?$/i;
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveal = element => {
    for (let parent = element?.parentElement; parent; parent = parent.parentElement) {
      if (parent.tagName === 'DETAILS') parent.open = true;
    }
  };
  const focusField = field => {
    reveal(field);
    field.scrollIntoView({ block: 'center', behavior: 'auto' });
    field.focus({ preventScroll: true });
  };

  const initComparison = root => {
    const buttons = Array.from(root.querySelectorAll('[data-asb-freight]'));
    const result = root.querySelector('[data-asb-result]');
    if (!buttons.length || !result) return;
    const resultTitle = result.querySelector('[data-asb-result-title]');
    const resultBody = result.querySelector('[data-asb-result-body]');
    const copy = key => result.dataset[key] || root.dataset[key];
    const update = selected => {
      const freight = Number(selected.dataset.asbFreight);
      if (![0, 15, 30].includes(freight)) return;
      const otherTotal = 105 + freight;
      const difference = otherTotal - 120;
      const state = difference < 0 ? 'lower' : difference > 0 ? 'higher' : 'equal';
      const replaceAmount = value => value?.split('__AMOUNT__').join(currency.format(Math.abs(difference)));
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button === selected)));
      root.querySelectorAll('[data-asb-other-total]').forEach(element => { element.textContent = currency.format(otherTotal); });
      root.querySelectorAll('[data-asb-other-freight]').forEach(element => { element.textContent = currency.format(freight); });
      const title = replaceAmount(copy(`${state}Title`));
      const body = replaceAmount(copy(`${state}Text`));
      if (resultTitle && title) resultTitle.textContent = title;
      if (resultBody && body) resultBody.textContent = body;
      result.dataset.asbPriceState = state;
    };
    buttons.forEach(button => button.addEventListener('click', () => update(button)));
    update(buttons.find(button => button.getAttribute('aria-pressed') === 'true')
      || buttons.find(button => button.dataset.asbFreight === '30') || buttons[0]);
  };

  const initForm = root => {
    const form = root.querySelector('#ASPriceBeatForm');
    if (!form) return;
    const fields = fieldIds.map(id => form.querySelector(`#${id}`)).filter(Boolean);
    const requiredFields = fields.filter(field => field.required);
    const summary = form.querySelector('#asb-form-errors');
    const errorList = summary?.querySelector('[data-asb-error-list]');
    const errorTitle = summary?.querySelector('[data-asb-error-title]');
    const inlineError = field => form.querySelector(`#${field.id}-error`);
    const customValidation = Boolean(summary && errorList && errorTitle
      && requiredFields.length === 7 && requiredFields.every(inlineError));
    const state = root.querySelector('[data-asb-form-state]');
    const recoveryKey = `asb-price-beat:${location.pathname}:${form.id}`;
    let attempted = state?.dataset.asbFormState === 'error';
    let errorSignature = null;

    const removeRecovery = () => {
      try { sessionStorage.removeItem(recoveryKey); } catch (_) {}
    };
    const saveRecovery = () => {
      const values = Object.fromEntries(fields.map(field => [field.id, field.type === 'checkbox' ? field.checked : field.value]));
      try {
        sessionStorage.setItem(recoveryKey, JSON.stringify({ version: 1, savedAt: Date.now(), values }));
      } catch (_) {}
    };
    const restoreRecovery = () => {
      try {
        const raw = sessionStorage.getItem(recoveryKey);
        if (!raw) return;
        const saved = JSON.parse(raw);
        const age = Date.now() - saved.savedAt;
        if (saved.version !== 1 || !Number.isFinite(saved.savedAt) || age < 0
          || age > recoveryLifetime || !saved.values || typeof saved.values !== 'object') {
          removeRecovery();
          return;
        }
        fields.forEach(field => {
          const value = saved.values[field.id];
          if (field.type === 'checkbox' && typeof value === 'boolean') field.checked = value;
          else if (field.type !== 'checkbox' && typeof value === 'string') field.value = value;
          if (value && !field.required) reveal(field);
        });
      } catch (_) { removeRecovery(); }
    };

    if (state?.dataset.asbFormState === 'success') removeRecovery();
    else if (state?.dataset.asbFormState === 'error') restoreRecovery();
    if (!fields.length) return;

    const normalize = field => {
      if (field.dataset.asbKind === 'money') {
        field.setCustomValidity('');
        const value = field.value.trim();
        if (!value) { field.value = ''; return; }
        const plainAmount = value.replace(/[$,\s]/g, '');
        const amount = Number(plainAmount);
        if (moneyPattern.test(value) && Number.isFinite(amount) && amount >= 0.01) {
          field.value = plainAmount;
        } else {
          field.setCustomValidity(field.dataset.asbInvalid || field.dataset.asbRequired || field.validationMessage);
        }
      } else if (field.type === 'url') {
        const value = field.value.trim();
        field.value = domainPattern.test(value) ? `https://${value}` : value;
      }
    };
    const messageFor = field => field.validity.valueMissing
      ? field.dataset.asbRequired || field.validationMessage
      : field.dataset.asbInvalid || field.validationMessage;
    const clearFieldError = field => {
      const error = inlineError(field);
      if (error) { error.hidden = true; error.textContent = ''; }
      field.removeAttribute('aria-invalid');
    };
    const showFieldError = field => {
      const error = inlineError(field);
      if (!error) return;
      if (field.validity.valid) { clearFieldError(field); return; }
      error.textContent = messageFor(field);
      error.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      reveal(field);
    };
    const syncSummary = () => {
      if (!customValidation) return;
      const errors = requiredFields.filter(field => {
        const error = inlineError(field);
        return error && !error.hidden && error.textContent;
      });
      const signature = JSON.stringify(errors.map(field => [field.id, inlineError(field).textContent]));
      if (signature === errorSignature) return;
      errorSignature = signature;
      errorList.replaceChildren();
      errors.forEach(field => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const label = field.dataset.asbLabel || field.labels?.[0]?.textContent.trim().replace(/\s+/g, ' ') || field.id;
        link.href = `#${field.id}`;
        link.textContent = `${label}: ${inlineError(field).textContent}`;
        link.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          focusField(field);
        });
        item.append(link);
        errorList.append(item);
      });
      summary.hidden = errors.length === 0;
    };

    if (customValidation) form.noValidate = true;
    fields.forEach(field => {
      field.addEventListener('blur', () => {
        normalize(field);
        if (customValidation && attempted) { showFieldError(field); syncSummary(); }
      });
      field.addEventListener('input', () => {
        if (field.dataset.asbKind === 'money') field.setCustomValidity('');
        if (customValidation && attempted) { clearFieldError(field); syncSummary(); }
      });
    });
    form.addEventListener('submit', event => {
      fields.forEach(normalize);
      const invalidFields = fields.filter(field => !field.validity.valid);
      if (invalidFields.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        attempted = true;
        if (customValidation && invalidFields.every(inlineError)) {
          requiredFields.forEach(showFieldError);
          syncSummary();
          summary.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
          summary.focus({ preventScroll: true });
        } else {
          form.reportValidity();
        }
        return;
      }
      if (customValidation) { requiredFields.forEach(clearFieldError); syncSummary(); }
      saveRecovery();
    }, true);
  };

  const init = scope => {
    const roots = scope.matches?.('.asb') ? [scope] : Array.from(scope.querySelectorAll?.('.asb') || []);
    roots.forEach(root => {
      if (root.dataset.asbReady === 'v2') return;
      root.dataset.asbReady = 'v2';
      initComparison(root);
      initForm(root);
      ['ASPriceBeatRequest', 'asb-comparison'].forEach(id => {
        const target = root.querySelector(`#${id}`);
        const title = target?.querySelector('h2');
        if (!target || !title) return;
        title.setAttribute('tabindex', '-1');
        root.querySelectorAll(`a[href="#${id}"]`).forEach(link => {
          link.addEventListener('click', event => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            history.replaceState(null, '', `#${id}`);
            target.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
            title.focus({ preventScroll: true });
          });
        });
      });
      const state = root.querySelector('[data-asb-form-state]');
      if (state) {
        reveal(state);
        state.setAttribute('tabindex', '-1');
        root.querySelector('#ASPriceBeatRequest')?.scrollIntoView({ block: 'start', behavior: 'auto' });
        state.focus({ preventScroll: true });
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();


